/**
 * Адаптер для преобразования User между Backend и Frontend форматами
 * Backend возвращает: firstName, lastName, name
 * Frontend ожидает: fullName
 */

// Тип User как его возвращает Backend
export interface BackendUser {
  id: string;
  email: string;
  firstName?: string;
  lastName?: string;
  name?: string;
  role: string;
  status?: string;
  avatar?: string;
  phone?: string;
  department?: string;
  position?: string;
  isEmailVerified?: boolean;
  createdAt?: string;
  updatedAt?: string;
}

// Тип User для использования во Frontend
export interface FrontendUser {
  id: string;
  email: string;
  fullName: string;
  role: string;
  status?: string | undefined;
  avatar?: string | undefined;
  phone?: string | undefined;
  department?: string | undefined;
  position?: string | undefined;
  isEmailVerified?: boolean | undefined;
  createdAt?: string | undefined;
  updatedAt?: string | undefined;
}

/**
 * Преобразует BackendUser в FrontendUser
 * Вычисляет fullName из name или firstName + lastName
 */
export function adaptUser(backendUser: BackendUser): FrontendUser {
  // Вычисляем fullName с правильным приоритетом
  let fullName = backendUser.name;
  if (!fullName) {
    const nameParts = [backendUser.firstName, backendUser.lastName].filter(Boolean);
    fullName = nameParts.length > 0 ? nameParts.join(' ') : backendUser.email;
  }

  return {
    id: backendUser.id,
    email: backendUser.email,
    fullName,
    role: backendUser.role,
    status: backendUser.status,
    avatar: backendUser.avatar,
    phone: backendUser.phone,
    department: backendUser.department,
    position: backendUser.position,
    isEmailVerified: backendUser.isEmailVerified,
    createdAt: backendUser.createdAt,
    updatedAt: backendUser.updatedAt,
  };
}

/**
 * Вычисляет отображаемое имя из объекта user (любого формата)
 * Безопасно для null/undefined
 */
export function getDisplayName(user: Partial<BackendUser & FrontendUser> | null | undefined): string {
  if (!user) return '—';

  // Проверяем fullName
  const frontendUser = user as FrontendUser;
  if (frontendUser.fullName) {
    return frontendUser.fullName;
  }

  // Проверяем name
  if (user.name) {
    return user.name;
  }

  // Собираем из firstName + lastName
  const nameParts = [user.firstName, user.lastName].filter(Boolean);
  if (nameParts.length > 0) {
    return nameParts.join(' ');
  }

  // Fallback на email
  return user.email || '—';
}

/**
 * Получает инициал для аватара
 */
export function getUserInitial(user: Partial<BackendUser & FrontendUser> | null | undefined): string {
  const name = getDisplayName(user);
  return name.charAt(0).toUpperCase() || '?';
}
